import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import { IconArrowBackUp } from "@tabler/icons-react";
import { mappingSnapshotStore } from "./MappingSnapshotStore";
import useToast from "hooks/useToast";
import { MEStore } from "features/MappingEditor/MappingEditorStore";

interface MappingSnapshotUndoProps {
  fileId: string | undefined;
}

function MappingSnapshotUndo({ fileId }: MappingSnapshotUndoProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { mappingSnapshots, loading, restoringId } = mappingSnapshotStore;

  React.useEffect(() => {
    if (fileId) {
      mappingSnapshotStore.listMappingSnapshots(fileId);
    }
  }, [fileId]);

  const handleUndo = async () => {
    if (!fileId || mappingSnapshots.length === 0) return;

    const snap = mappingSnapshots[0];

    // Trigger the Celery task
    const restoreResult = await mappingSnapshotStore.restoreSnapshot(
      fileId,
      snap.id,
      toast,
      t("mappingEditor.snapshotRestored")
    );

    if (!restoreResult.ok) {
      toast(
        restoreResult.message || t("commons.genericErrorMessage"),
        "warning"
      );
    }
  };

  const count = mappingSnapshots.length;
  const isRestoring = restoringId !== null;

  return (
    <Button
      type="gray"
      width="fit-content"
      leadingIcon={<IconArrowBackUp />}
      loading={loading || isRestoring}
      disable={count === 0 || MEStore.isUndoInProgress}
      onClick={handleUndo}
    >
      {t("mappingEditor.undo")}
      {count > 0 && (
        <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">
          {count}
        </span>
      )}
    </Button>
  );
}

export default observer(MappingSnapshotUndo);
